import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * Premium Alert Component - Swiss Luxury Aesthetic
 * Clean notifications with refined borders and precise spacing
 */
const Alert = React.forwardRef(({ className, variant = "default", ...props }, ref) => {
  const variants = {
    default: "bg-neutral-50 border-neutral-200 text-neutral-900 [&>svg]:text-neutral-600",
    info: "bg-info-50 border-info-200 text-info-900 [&>svg]:text-info-600",
    success: "bg-success-50 border-success-200 text-success-900 [&>svg]:text-success-600",
    warning: "bg-warning-50 border-warning-200 text-warning-900 [&>svg]:text-warning-600",
    danger: "bg-danger-50 border-danger-200 text-danger-900 [&>svg]:text-danger-600",
  };

  // Determine aria-live based on variant
  const ariaLive = variant === "danger" ? "assertive" : "polite";

  return (
    <div
      ref={ref}
      role="alert"
      aria-live={ariaLive}
      className={cn(
        "relative w-full rounded-xl border px-6 py-4 text-sm",
        "[&>svg]:absolute [&>svg]:left-6 [&>svg]:top-4 [&>svg]:h-5 [&>svg]:w-5",
        "[&>svg~*]:pl-8",
        variants[variant],
        className
      )}
      {...props}
    />
  );
});
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1.5 font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm leading-relaxed opacity-90 [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
